import {
  EditorContent,
  EditorContext,
  ReactNodeViewRenderer,
  useEditor,
} from "@tiptap/react";
import React, { useEffect, useRef, useState } from "react";

// --- Tiptap Core Extensions ---
import { Highlight } from "@tiptap/extension-highlight";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import Mention from "@tiptap/extension-mention";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import { TextAlign } from "@tiptap/extension-text-align";
import { Typography } from "@tiptap/extension-typography";
import { Selection } from "@tiptap/extensions";
import { StarterKit } from "@tiptap/starter-kit";

// --- UI Primitives ---
import { Button } from "app/components/TextEditor/components/tiptap-ui-primitive/button";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "app/components/TextEditor/components/tiptap-ui-primitive/toolbar";

// --- Tiptap Node ---
import "app/components/TextEditor/components/tiptap-node/blockquote-node/blockquote-node.scss";
import "app/components/TextEditor/components/tiptap-node/code-block-node/code-block-node.scss";
import "app/components/TextEditor/components/tiptap-node/heading-node/heading-node.scss";
import { HorizontalRule } from "app/components/TextEditor/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import "app/components/TextEditor/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "app/components/TextEditor/components/tiptap-node/image-node/image-node.scss";
import { ImageResizeNode } from "app/components/TextEditor/components/tiptap-node/image-resize-node/image-resize-node-extension";
import { ImageUploadNode } from "app/components/TextEditor/components/tiptap-node/image-upload-node/image-upload-node-extension";
import "app/components/TextEditor/components/tiptap-node/list-node/list-node.scss";
import "app/components/TextEditor/components/tiptap-node/paragraph-node/paragraph-node.scss";

// --- Tiptap UI ---
import { mediaUploadSaga } from "app/components/MediaUploader/redux/saga";
import {
  mediaUploadReducer,
  mediaUploadSlice,
} from "app/components/MediaUploader/redux/slice";
import { BlockquoteButton } from "app/components/TextEditor/components/tiptap-ui/blockquote-button";
import { CodeBlockButton } from "app/components/TextEditor/components/tiptap-ui/code-block-button";
import {
  ColorHighlightPopover,
  ColorHighlightPopoverButton,
  ColorHighlightPopoverContent,
} from "app/components/TextEditor/components/tiptap-ui/color-highlight-popover";
import { HeadingDropdownMenu } from "app/components/TextEditor/components/tiptap-ui/heading-dropdown-menu";
import { ImageUploadButton } from "app/components/TextEditor/components/tiptap-ui/image-upload-button";
import {
  LinkButton,
  LinkContent,
  LinkPopover,
} from "app/components/TextEditor/components/tiptap-ui/link-popover";
import { ListDropdownMenu } from "app/components/TextEditor/components/tiptap-ui/list-dropdown-menu";
import { MarkButton } from "app/components/TextEditor/components/tiptap-ui/mark-button";
import { TableFloatingMenu } from "app/components/TextEditor/components/tiptap-ui/table-floating-menu";
import { TextAlignButton } from "app/components/TextEditor/components/tiptap-ui/text-align-button";
import { UndoRedoButton } from "app/components/TextEditor/components/tiptap-ui/undo-redo-button";

// --- Icons ---
import { ArrowLeftIcon } from "app/components/TextEditor/components/tiptap-icons/arrow-left-icon";
import { HighlighterIcon } from "app/components/TextEditor/components/tiptap-icons/highlighter-icon";
import { LinkIcon } from "app/components/TextEditor/components/tiptap-icons/link-icon";

// --- Hooks ---
import { useCursorVisibility } from "app/components/TextEditor/hooks/use-cursor-visibility";
import { useIsBreakpoint } from "app/components/TextEditor/hooks/use-is-breakpoint";
import { useWindowSize } from "app/components/TextEditor/hooks/use-window-size";

// --- Lib ---
import {
  handleImageUpload,
  MAX_FILE_SIZE,
} from "app/components/TextEditor/lib/tiptap-utils";

// --- Styles ---
import "app/components/TextEditor/components/tiptap-templates/simple/simple-editor.scss";

import { createMentionSuggestion } from "app/components/TextEditor/components/mentionSuggestion";
import MentionNodeView from "app/components/TextEditor/components/mentionSuggestion/components/MentionNodeView";
import { InsertTableButton } from "app/components/TextEditor/components/tiptap-ui/insert-table-button";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { TextEditorProps } from "./types";
import clsx from "clsx";

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  isMobile,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  isMobile: boolean;
}) => {
  return (
    <>
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu
          types={["bulletList", "orderedList", "taskList"]}
          portal={isMobile}
        />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <InsertTableButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageUploadButton text="Add" />
      </ToolbarGroup>
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: "highlighter" | "link";
  onBack: () => void;
}) => (
  <>
    <ToolbarGroup>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
);

export function SimpleEditor({
  initialContent,
  onChange,
  placeholder = "Type @ to mention someone or a ticket...",
  readonly = false,
  onBlur,
  onFocus,
  onEditorReady,
  mentionDropdownComponent,
  className,
  containerOnClick,
  onAttachmentClick,
  onAttachmentRename,
  onAttachmentDelete,
  autoFocus = false,
}: TextEditorProps) {
  useInjectReducer({ key: mediaUploadSlice, reducer: mediaUploadReducer });
  useInjectSaga({ key: mediaUploadSlice, saga: mediaUploadSaga });

  const isMobile = useIsBreakpoint();
  const { height } = useWindowSize();
  const [mobileView, setMobileView] = useState<"main" | "highlighter" | "link">(
    "main",
  );
  const toolbarRef = useRef<HTMLDivElement>(null);
  const onAttachmentRenameRef = useRef(onAttachmentRename);
  const onAttachmentDeleteRef = useRef(onAttachmentDelete);
  const onAttachmentClickRef = useRef(onAttachmentClick);

  useEffect(() => {
    onAttachmentRenameRef.current = onAttachmentRename;
  }, [onAttachmentRename]);

  useEffect(() => {
    onAttachmentDeleteRef.current = onAttachmentDelete;
  }, [onAttachmentDelete]);

  useEffect(() => {
    onAttachmentClickRef.current = onAttachmentClick;
  }, [onAttachmentClick]);

  const editor = useEditor({
    immediatelyRender: false,
    autofocus: autoFocus,
    editable: !readonly,
    onUpdate({ editor }) {
      onChange?.({
        html: editor.getHTML(),
        json: editor.getJSON(),
        text: editor.getText(),
      });
    },
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Typography,
      Superscript,
      Subscript,
      Selection,
      Placeholder.configure({
        placeholder,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention",
        },
        suggestion: createMentionSuggestion({
          dropdownComponent: mentionDropdownComponent,
        }),
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            type: {
              default: null,
              parseHTML: (element) => element.getAttribute("data-target-type"),
              renderHTML: (attributes) => {
                if (!attributes.type) {
                  return {};
                }

                return {
                  "data-target-type": attributes.type,
                };
              },
            },
            ticketId: {
              default: null,
              parseHTML: (element) => element.getAttribute("data-ticket--id"),
              renderHTML: (attributes) =>
                attributes.ticketId
                  ? { "data-ticket--id": attributes.ticketId }
                  : {},
            },
            ticketTitle: {
              default: null,
              parseHTML: (element) => element.getAttribute("data-ticket-title"),
              renderHTML: (attributes) =>
                attributes.ticketTitle
                  ? { "data-ticket-title": attributes.ticketTitle }
                  : {},
            },
            ticketStatusName: {
              default: null,
              parseHTML: (element) =>
                element.getAttribute("data-ticket-status-name"),
              renderHTML: (attributes) =>
                attributes.ticketStatusName
                  ? { "data-ticket-status-name": attributes.ticketStatusName }
                  : {},
            },
            ticketStatusColor: {
              default: null,
              parseHTML: (element) =>
                element.getAttribute("data-ticket-status-color"),
              renderHTML: (attributes) =>
                attributes.ticketStatusColor
                  ? { "data-ticket-status-color": attributes.ticketStatusColor }
                  : {},
            },
            ticketPriority: {
              default: null,
              parseHTML: (element) =>
                element.getAttribute("data-ticket-priority"),
              renderHTML: (attributes) =>
                attributes.ticketPriority
                  ? { "data-ticket-priority": attributes.ticketPriority }
                  : {},
            },
            ticketTypeName: {
              default: null,
              parseHTML: (element) =>
                element.getAttribute("data-ticket-type-name"),
              renderHTML: (attributes) =>
                attributes.ticketTypeName
                  ? { "data-ticket-type-name": attributes.ticketTypeName }
                  : {},
            },
            ticketTypeIcon: {
              default: null,
              parseHTML: (element) =>
                element.getAttribute("data-ticket-type-icon"),
              renderHTML: (attributes) =>
                attributes.ticketTypeIcon
                  ? { "data-ticket-type-icon": attributes.ticketTypeIcon }
                  : {},
            },
            projectId: {
              default: null,
              parseHTML: (element) => element.getAttribute("data-project-id"),
              renderHTML: (attributes) =>
                attributes.projectId
                  ? { "data-project-id": attributes.projectId }
                  : {},
            },
            teamId: {
              default: null,
              parseHTML: (element) => element.getAttribute("data-team-id"),
              renderHTML: (attributes) =>
                attributes.teamId ? { "data-team-id": attributes.teamId } : {},
            },
          };
        },

        addNodeView() {
          return ReactNodeViewRenderer(MentionNodeView);
        },
      }),
      ImageResizeNode.configure({
        allowResize: true,
        maintainAspectRatio: true,
        minWidth: 50,
        minHeight: 50,
        isEditable: !readonly,
        onAttachmentRename: (payload) =>
          onAttachmentRenameRef.current?.(payload),
        onAttachmentDelete: (payload) =>
          onAttachmentDeleteRef.current?.(payload),
        onAttachmentClick: (payload) =>
          onAttachmentClickRef.current?.(payload) ?? false,
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            imageId: {
              default: null,
              parseHTML: (element) => element.getAttribute("data-image-id"),
              renderHTML: (attributes) => {
                if (!attributes.imageId) return {};
                return {
                  "data-image-id": attributes.imageId,
                };
              },
            },
          };
        },
      }),
      ImageUploadNode.configure({
        type: "image",
        accept: "image/*",
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error("Upload failed:", error),
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialContent || "",
  });

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  useEffect(() => {
    editor?.setEditable(!readonly);
  }, [editor, readonly]);

  useEffect(() => {
    if (!editor) return;

    const normalizedContent = initialContent || "";
    const currentJson = editor.getJSON();
    const hasSameContent =
      typeof normalizedContent === "string"
        ? editor.getHTML() === normalizedContent
        : JSON.stringify(currentJson) === JSON.stringify(normalizedContent);

    if (hasSameContent) return;

    editor.commands.setContent(normalizedContent, {
      emitUpdate: false,
    });
  }, [editor, initialContent]);

  useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main");
    }
  }, [isMobile, mobileView]);

  useEffect(() => {
    onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  const handleEditorContainerClickCapture = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!onAttachmentClick) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest(".attachment-header")) return;

    const anchorElement = target.closest("a") as HTMLAnchorElement | null;
    const imageElement = target.closest("img") as HTMLImageElement | null;
    const imageIdElement = target.closest("[data-image-id]") as HTMLElement | null;

    const imageId = imageIdElement?.getAttribute("data-image-id") || undefined;
    let clickedUrl: string | undefined;

    if (anchorElement?.href) {
      clickedUrl = anchorElement.href;
    } else if (imageElement?.src) {
      clickedUrl = imageElement.src;
    }

    if (!clickedUrl && !imageId) return;

    const handled = onAttachmentClick({
      url: clickedUrl,
      imageId,
    });
    if (!handled) return;

    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      className={`simple-editor-wrapper  ${!readonly ? "border border-gray-100 rounded-md bg-white" : "simple-editor-wrapper-with-edit"}`}
      onClick={containerOnClick}
      onClickCapture={handleEditorContainerClickCapture}
    >
      <EditorContext.Provider value={{ editor }}>
        {!readonly ? (
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                    bottom: `calc(100% - ${height - rect.y}px)`,
                  }
                : {}),
            }}
          >
            {mobileView === "main" ? (
              <MainToolbarContent
                onHighlighterClick={() => setMobileView("highlighter")}
                onLinkClick={() => setMobileView("link")}
                isMobile={isMobile}
              />
            ) : (
              <MobileToolbarContent
                type={mobileView === "highlighter" ? "highlighter" : "link"}
                onBack={() => setMobileView("main")}
              />
            )}
          </Toolbar>
        ) : null}

        <EditorContent
          editor={editor}
          role="presentation"
          className={clsx(
            "simple-editor-content",
            { "readonly-mode": readonly },
            className,
          )}
          onBlur={onBlur}
          onFocus={onFocus}
        />

        <TableFloatingMenu editor={editor} isEditable={readonly} />
      </EditorContext.Provider>
    </div>
  );
}
